import { useEffect, useState } from 'react';
import { Experiment, MarkViewedOn, VariantOf, VariantsConfig } from './Experiment';

//---------------------------------------------------------------------------
/**
 * The `<Experiment>` & `<Variant>` are the primary interfaces for running an experiment.
 *
 * Sometimes it is more convenient to be able to programmatically query an experiment
 *
 * This provides a hook that allows you to get the current variant for an experiment
 *
 *
 * Usage:
 * ```tsx
 * const variant = useExperiment(Experiments.MyExperiment)
 * ```
 *
 * The experiment must have already been started (ie you need the relevant `<Experiment>` component
 * to have been mounted above this somewhere) otherwise it will return undefined
 */
export function useExperiment<Variants extends VariantsConfig>(
  experiment: Experiment<Variants>,
  options?: { suppressViewTracking: boolean }
): VariantOf<Variants> {
  const [currentVariant, setCurrentVariant] = useState(experiment.variant);

  useEffect(
    function setupVariantListener() {
      const onVariantChange = () => setCurrentVariant(experiment.variant);

      experiment.onChange.addListener(onVariantChange);
      return () => experiment.onChange.removeListener(onVariantChange);
    },
    [experiment]
  );

  useEffect(
    function markExperimentViewed() {
      if (
        experiment.markViewedOn === MarkViewedOn.React &&
        !(options?.suppressViewTracking ?? false)
      ) {
        experiment.viewed();
      }
    },
    [currentVariant]
  );

  return currentVariant;
}

export function useExperimentSelect<
  VC extends VariantsConfig,
  Values extends Record<VariantOf<VC>, unknown>,
>(
  experiment: Experiment<VC>,
  values: Values,
  options?: { suppressViewTracking: boolean }
): Values[keyof Values] {
  const variant = useExperiment(experiment, options);
  return values[variant];
}
